import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, CircularProgress, withTheme, Chip, Typography } from '@material-ui/core';
import { ErrorIcon } from '../../../icons';

import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import { useHistory } from 'react-router';
import FormSection from '../../../components/atom/FormSection';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import AutocompleteSelect from '../../../components/atom/AutocompleteSelect';
import { onlyClientAutocomplete, chainAutocomplete } from '../../../autocomplete';

function SourcegroupEditPage({ theme }) {
    const { sourceGroupId } = useParams();
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();

    // the first "false" disables this hook in here
    const hasId = !!sourceGroupId;
    const [item, loading, error, setItem] = useSwaggerClient(
        hasId ? 'sourcegroups' : false,
        'getSourceGroup',
        {
            sourceGroupId: parseInt(sourceGroupId, 10)
        }
    );
    // Fetch sources for this source group
    const [sources, sourcesLoading, sourcesError] = useSwaggerClient(
        hasId ? 'sources' : false,
        'listSources',
        {
            sourceGroupId: parseInt(sourceGroupId, 10),
            page: 0,
            pageSize: 1000
        }
    );

    useEffect(() => {
        if (hasId) {
            return;
        }
        // setItem to default values
        setItem({
            name: '',
            companyId: null,
            chainId: null
        });
    }, [hasId]);

    const modifyItem = () => {
        doSwaggerCall('sourcegroups', hasId ? 'updateSourceGroup' : 'createSourceGroup', {
            sourceGroupId: hasId ? parseInt(sourceGroupId, 10) : null,
            sourceGroup: {
                name: item.name,
                companyId: item.companyId,
                chainId: item.chainId
            }
        })
            .then(_res => {
                notifySuccess('Source group modified!');
                history.push(`/admin/sourcegroups`);
            })
            .catch(err => {
                console.log(err);
                notifyError(err?.response?.body?.message || 'Error while modifying source group!');
            });
    };

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }
    if ((loading && hasId) || item === null) {
        return (
            <Grid container justifyContent="center">
                <CircularProgress />
            </Grid>
        );
    }

    return (
        <Grid className="bg-white activity-edit">
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                }}
            >
                <Grid container spacing={2}>
                    <AdminHeader title="Source group" />
                    {!!error && (
                        <Grid
                            item
                            container
                            spacing={2}
                            xs={12}
                            style={{
                                color: theme.palette.error.main
                            }}
                            alignItems="center"
                        >
                            <Grid item>
                                <ErrorIcon color="error" />
                            </Grid>
                            <Grid item>
                                <span>{error.response.body.message}</span>
                            </Grid>
                        </Grid>
                    )}
                    <FormSection title="Generic information">
                        <Grid item xs={12}>
                            <TextField
                                label="Name"
                                fullWidth
                                required
                                type="text"
                                value={item?.name}
                                onChange={ev => setItem({ ...item, name: ev.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AutocompleteSelect
                                initCall={onlyClientAutocomplete.init}
                                autocompleteCall={onlyClientAutocomplete.search}
                                value={item?.companyId}
                                setValue={value => setItem({ ...item, companyId: value })}
                            >
                                <TextField label="Company" />
                            </AutocompleteSelect>
                        </Grid>
                        <Grid item xs={6}>
                            <AutocompleteSelect
                                initCall={chainAutocomplete.init}
                                autocompleteCall={chainAutocomplete.search}
                                value={item?.chainId}
                                setValue={value => setItem({ ...item, chainId: value })}
                            >
                                <TextField label="Chain" />
                            </AutocompleteSelect>
                        </Grid>
                    </FormSection>
                    {hasId && (
                        <FormSection title="Sources">
                            <Grid item xs={12}>
                                {sourcesLoading ? (
                                    <CircularProgress size={24} />
                                ) : sourcesError ? (
                                    <Typography
                                        variant="body2"
                                        style={{ color: theme.palette.error.main }}
                                    >
                                        Error loading sources
                                    </Typography>
                                ) : sources?.items?.length > 0 ? (
                                    <Grid
                                        style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}
                                    >
                                        {sources.items.map(source => (
                                            <Chip
                                                key={source.id}
                                                label={source.name}
                                                clickable
                                                onClick={() =>
                                                    window.open(
                                                        `/admin/sources/update/${source.id}`,
                                                        '_blank'
                                                    )
                                                }
                                                color="primary"
                                            />
                                        ))}
                                    </Grid>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        style={{ color: theme.palette.text.secondary }}
                                    >
                                        No sources in this group
                                    </Typography>
                                )}
                            </Grid>
                        </FormSection>
                    )}
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={!item.name}
                            onClick={modifyItem}
                        >
                            Save
                        </Button>
                    </Grid>
                    <Grid item xs={6}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => history.push(`/admin/sourcegroups`)}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Grid>
    );
}

export default withTheme(SourcegroupEditPage);
